import React, { useState, useMemo, useEffect } from 'react';
import { X, Bell, Filter, CheckCircle, Info } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification, useNotificationState, NOTIFICATION_CATEGORIES } from './NotificationSystem';
import { getCategoryLabel, getUITranslation, getTitleTranslation, getMessageTranslation } from '../../utils/notificationTranslations';
import { usePresenceTransition } from '../../hooks/usePresenceTransition';
import '../../styles/components/notification-center.css';

const NotificationCenter = ({ isOpen, onClose }) => {
  // Close notification center when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.notification-center')) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  
  const { currentLanguage, isRTL, t } = useLanguage();
  const tn = (key) => t(`notificationCenter.${key}`);
  const { removeNotification, markAsRead, clearAllNotifications } = useNotification();
  const { persistentNotifications } = useNotificationState();
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [showRead, setShowRead] = useState(true);

  const filteredNotifications = useMemo(() => {
    let filtered = persistentNotifications;
    
    // Filter by category
    if (selectedCategory !== 'ALL') {
      filtered = filtered.filter(n => n.category === selectedCategory);
    }
    
    // Filter by read status
    if (!showRead) {
      filtered = filtered.filter(n => !n.read);
    }
    
    return filtered.sort((a, b) => {
      const aMs = Date.parse(a.createdAt);
      const bMs = Date.parse(b.createdAt);
      const safeA = Number.isFinite(aMs) ? aMs : 0;
      const safeB = Number.isFinite(bMs) ? bMs : 0;
      return safeB - safeA;
    });
  }, [persistentNotifications, selectedCategory, showRead]);

  const unreadCount = useMemo(() => {
    return persistentNotifications.filter(n => !n.read).length;
  }, [persistentNotifications]);

  const getCategoryTone = (category) =>
    NOTIFICATION_CATEGORIES[category]?.tone || 'info';

  const getCategoryIcon = (category) => {
    if (NOTIFICATION_CATEGORIES[category]) {
      const meta = NOTIFICATION_CATEGORIES[category];
      const Icon = meta.icon;
      return <Icon size={18} strokeWidth={2.5} className={`ns-tone-icon ns-tone-icon--${meta.tone}`} aria-hidden />;
    }
    return <Info size={18} strokeWidth={2.5} className="ns-tone-icon ns-tone-icon--muted" aria-hidden />;
  };

  const getCategoryLabelTranslated = (category) => {
    return getCategoryLabel(category, currentLanguage);
  };

  // Translate notification titles saved earlier in English or French
  const getTranslatedTitle = (title) => {
    const legacyMappings = [
      {
        keys: ['Product Expired', 'Produit Expiré'],
        translationKey: 'productExpired'
      },
      {
        keys: ['Product Expiring Soon', 'Produit Expirant Bientôt'],
        translationKey: 'productExpiring'
      },
      {
        keys: ['Reorder Needed', 'Réapprovisionnement Nécessaire'],
        translationKey: 'reorderNeeded'
      },
      {
        keys: ['Delivery Updates', 'Mise à Jour de Livraison'],
        translationKey: 'deliveryUpdate'
      },
      {
        keys: ['System Error', 'Erreur Système'],
        translationKey: 'systemError'
      }
    ];

    const match = legacyMappings.find(({ keys }) => keys.includes(title));
    if (match) {
      return getTitleTranslation(match.translationKey, currentLanguage);
    }

    return title;
  };

  // Translate notification message if it's in French
  const getTranslatedMessage = (message, category) => {
    if (!message || currentLanguage === 'fr') {
      return message;
    }

    // Try to extract data from French message patterns
    // Pattern: "ProductName (Lot: batchNumber) expire dans X jour(s) (Y unités)"
    // More flexible pattern to handle various spacing
    const expiringPattern = /^(.+?)\s*\(Lot:\s*([^)]+)\)\s*expire\s+dans\s+(\d+)\s+jour\(s\)\s*\((\d+)\s+unités?\)$/i;
    const expiredPattern = /^(.+?)\s*\(Lot:\s*([^)]+)\)\s*a\s+expiré\s*\((\d+)\s+unités?\)$/i;
    const reorderPattern = /^(.+?)\s*nécessite\s+un\s+réapprovisionnement\s*\(Stock:\s*(\d+)\)$/i;
    const deliveryPatternToday = /^Commande\s+(\w+)\s+livraison\s+prévue\s+aujourd'hui$/i;
    const deliveryPatternDays = /^Commande\s+(\w+)\s+livraison\s+prévue\s+dans\s+(\d+)\s+jour\(s\)$/i;

    let match;
    
    if (category === 'EXPIRING' && (match = message.match(expiringPattern))) {
      const [, productName, batchNumber, days, quantity] = match;
      return getMessageTranslation('productExpiring', currentLanguage, productName.trim(), batchNumber.trim(), parseInt(days), parseInt(quantity));
    }
    
    if (category === 'EXPIRED' && (match = message.match(expiredPattern))) {
      const [, productName, batchNumber, quantity] = match;
      return getMessageTranslation('productExpired', currentLanguage, productName.trim(), batchNumber.trim(), parseInt(quantity));
    }
    
    if (category === 'REORDER' && (match = message.match(reorderPattern))) {
      const [, productName, stock] = match;
      return getMessageTranslation('reorderNeeded', currentLanguage, productName.trim(), parseInt(stock));
    }
    
    if (category === 'DELIVERY') {
      if ((match = message.match(deliveryPatternToday))) {
        const [, orderId] = match;
        return getMessageTranslation('deliveryUpdate', currentLanguage, orderId.trim(), 0);
      }
      if ((match = message.match(deliveryPatternDays))) {
        const [, orderId, days] = match;
        return getMessageTranslation('deliveryUpdate', currentLanguage, orderId.trim(), parseInt(days));
      }
    }

    return message;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (!Number.isFinite(date.getTime())) {
      return getUITranslation('now', currentLanguage);
    }
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return getUITranslation('now', currentLanguage);
    } else if (diffInHours < 24) {
      return getUITranslation('hoursAgo', currentLanguage, diffInHours);
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return getUITranslation('daysAgo', currentLanguage, diffInDays);
    }
  };

  const { present, closing } = usePresenceTransition(Boolean(isOpen));
  if (!present) return null;

  return (
    <div
      className={`notification-center nc-panel${closing ? ' nc-panel--leaving' : ''}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-ads-modal-state={closing ? 'closing' : 'open'}
    >
      {/* Header */}
      <div className="nc-header">
        <div className="nc-header-left">
          <div className="nc-header-icon">
            <Bell size={22} strokeWidth={2.5} className="ns-tone-icon ns-tone-icon--inverse" aria-hidden />
          </div>
          <div>
            <span className="nc-header-title">
              {getUITranslation('notifications', currentLanguage)}
            </span>
            {unreadCount > 0 && (
              <span className="nc-header-subtitle">
                {unreadCount} {unreadCount === 1 ? tn('unreadSingular') : tn('unreadPlural')}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <span className="nc-unread-badge">
              {unreadCount}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="nc-close-btn"
        >
          <X size={18} />
        </button>
      </div>

      {/* Filters */}
      <div className="nc-filters">
        <div className="nc-filter-label-row">
          <Filter size={14} className="ns-tone-icon ns-tone-icon--muted" aria-hidden />
          <span className="nc-filter-label">
            {getUITranslation('filter', currentLanguage)}
          </span>
        </div>
        
        {/* Category Filter */}
        <div className="nc-category-filters">
          <button
            type="button"
            onClick={() => setSelectedCategory('ALL')}
            className={`nc-filter-btn nc-filter-btn--all${selectedCategory === 'ALL' ? ' nc-filter-btn--selected' : ''}`}
          >
            {getUITranslation('all', currentLanguage)}
          </button>
          {Object.keys(NOTIFICATION_CATEGORIES).map(category => (
            <button
              key={category}
              type="button"
              onClick={() => setSelectedCategory(category)}
              className={`nc-filter-btn nc-filter-btn--category nc-tone nc-tone--${getCategoryTone(category)}${selectedCategory === category ? ' nc-filter-btn--selected' : ''}`}
            >
              {getCategoryIcon(category)}
            </button>
          ))}
        </div>

        {/* Read/Unread Filter */}
        <div className="nc-read-filter-row">
          <label className="nc-read-label">
            <input
              type="checkbox"
              className="nc-checkbox"
              checked={showRead}
              onChange={(e) => setShowRead(e.target.checked)}
            />
            {getUITranslation('showRead', currentLanguage)}
          </label>
        </div>
      </div>

      {/* Notifications List */}
      <div className="nc-list">
        {filteredNotifications.length === 0 ? (
          <div className="nc-empty">
            <div className="nc-empty-icon-wrap">
              <Bell size={32} className="ns-tone-icon ns-tone-icon--muted" aria-hidden />
            </div>
            <div className="nc-empty-title">
              {getUITranslation('noNotifications', currentLanguage)}
            </div>
            <div className="nc-empty-subtext">
              {getUITranslation('noNotificationsSubtext', currentLanguage)}
            </div>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`nc-item nc-tone nc-tone--${getCategoryTone(notification.category)}${notification.read ? ' nc-item--read' : ' nc-item--unread'}`}
            >
              {!notification.read && (
                <div
                  className={`nc-unread-bar nc-tone nc-tone--${getCategoryTone(notification.category)}`}
                />
              )}
              <div className="nc-item-row">
                <div
                  className={`nc-item-icon-wrap nc-tone nc-tone--${getCategoryTone(notification.category)}`}
                >
                  {getCategoryIcon(notification.category)}
                </div>
                
                <div className="nc-item-content">
                  <div className={`nc-item-title${notification.read ? ' nc-item-title--read' : ' nc-item-title--unread'}`}>
                    {getTranslatedTitle(notification.title)}
                  </div>
                  
                  {notification.message && (
                    <div className={`nc-item-message${notification.read ? ' nc-item-message--read' : ' nc-item-message--unread'}`}>
                      {getTranslatedMessage(notification.message, notification.category)}
                    </div>
                  )}
                  
                  <div className="nc-item-meta">
                    <span
                      className={`nc-category-badge nc-tone nc-tone--${getCategoryTone(notification.category)}`}
                    >
                      {getCategoryLabelTranslated(notification.category)}
                    </span>
                    
                    <span className="nc-item-date">
                      {formatDate(notification.createdAt)}
                    </span>
                  </div>
                </div>
                
                <div className="nc-item-actions">
                  {!notification.read && (
                    <button
                      type="button"
                      onClick={() => markAsRead(notification.id)}
                      className="nc-mark-read-btn"
                      title={getUITranslation('markAsReadTooltip', currentLanguage)}
                    >
                      <CheckCircle size={16} strokeWidth={2.5} />
                    </button>
                  )}
                  
                  <button
                    type="button"
                    onClick={() => removeNotification(notification.id)}
                    className="nc-delete-btn"
                    title={getUITranslation('deleteTooltip', currentLanguage)}
                  >
                    <X size={16} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {filteredNotifications.length > 0 && (
        <div className="nc-footer">
          <span className="nc-footer-count">
            {getUITranslation('notificationCount', currentLanguage, filteredNotifications.length)}
          </span>
          
          <button
            type="button"
            onClick={clearAllNotifications}
            className="nc-clear-all-btn"
          >
            {getUITranslation('clearAll', currentLanguage)}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
